/**
 * Configurer Vuex Module
 * Manages state for the MSDAT Dashboard Configurer
 */

import db, { auth } from '@/config/firebase';

const DASHBOARD_COLLECTION = 'dashboard_instances';

// Helper to safely parse localStorage
const safeJsonParse = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (e) {
    console.warn(`Failed to parse localStorage key: ${key}`, e);
    return defaultValue;
  }
};

// Helper to safely save to localStorage
const safeJsonSave = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.warn(`Failed to save to localStorage key: ${key}`, e);
  }
};

const removeLocalItem = (key) => {
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
};

const safeSessionJsonParse = (key, defaultValue = null) => {
  try {
    const item = sessionStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (e) {
    console.warn(`Failed to parse sessionStorage key: ${key}`, e);
    return defaultValue;
  }
};

const safeSessionJsonSave = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.warn(`Failed to save to sessionStorage key: ${key}`, e);
  }
};

const getFirebaseUser = () => new Promise((resolve) => {
  if (auth.currentUser) {
    resolve(auth.currentUser);
    return;
  }

  const unsubscribe = auth.onAuthStateChanged((user) => {
    unsubscribe();
    resolve(user);
  });
});

const isPermissionDenied = (error) => error?.code === 'permission-denied'
  || error?.message?.toLowerCase().includes('permission');

export default {
  namespaced: true,

  state: {
    // Dashboard configuration
    dashboardConfig: safeJsonParse('msdat_config', null),

    // Appearance/theme configuration
    appearanceConfig: safeJsonParse('msdat_appearance', {
      theme: 'light',
      colors: {
        primary: '#007bff',
        secondary: '#6c757d',
        accent: '#28a745',
        background: '#ffffff',
        text: '#212529',
      },
      logo: null,
      fontFamily: 'Inter, sans-serif',
    }),

    // Raw Excel data (sheets object)
    rawExcelData: safeSessionJsonParse('msdat_raw_data', {}),

    // Selected sheet name
    selectedSheet: safeJsonParse('msdat_selected_sheet', null),

    // Column mapping configuration
    columnMappingConfig: safeJsonParse('msdat_column_mapping', null),

    // Transformed MSDAT-formatted data
    transformedData: safeJsonParse('msdat_transformed_data', []),

    // Data summary statistics
    dataSummary: safeJsonParse('msdat_data_summary', null),

    // Available sheet names
    sheetNames: safeJsonParse('msdat_sheet_names', []),

    // Saved dashboard instances
    instances: safeJsonParse('msdat_instances', []),

    // Current step in the wizard
    currentStep: safeJsonParse('msdat_current_step', 1),

    // Active Dashboard Document ID being edited
    activeInstanceId: safeJsonParse('msdat_active_instance', null),

    // User account info
    userAccount: safeJsonParse('msdat_user_account', null),
  },

  mutations: {
    // Dashboard Config
    SET_DASHBOARD_CONFIG(state, config) {
      state.dashboardConfig = config;
      safeJsonSave('msdat_config', config);
    },

    UPDATE_DASHBOARD_CONFIG(state, partialConfig) {
      state.dashboardConfig = { ...state.dashboardConfig, ...partialConfig };
      safeJsonSave('msdat_config', state.dashboardConfig);
    },

    // Appearance Config
    SET_APPEARANCE_CONFIG(state, config) {
      state.appearanceConfig = config;
      safeJsonSave('msdat_appearance', config);
    },

    UPDATE_APPEARANCE_CONFIG(state, partialConfig) {
      state.appearanceConfig = { ...state.appearanceConfig, ...partialConfig };
      safeJsonSave('msdat_appearance', state.appearanceConfig);
    },

    // Raw Excel Data
    SET_RAW_EXCEL_DATA(state, data) {
      state.rawExcelData = data;
      safeSessionJsonSave('msdat_raw_data', data);
      localStorage.removeItem('msdat_raw_data');
    },

    // Selected Sheet
    SET_SELECTED_SHEET(state, sheetName) {
      state.selectedSheet = sheetName;
      safeJsonSave('msdat_selected_sheet', sheetName);
    },

    // Sheet Names
    SET_SHEET_NAMES(state, names) {
      state.sheetNames = names;
      safeJsonSave('msdat_sheet_names', names);
    },

    // Column Mapping
    SET_COLUMN_MAPPING(state, mapping) {
      state.columnMappingConfig = mapping;
      safeJsonSave('msdat_column_mapping', mapping);
    },

    // Transformed Data
    SET_TRANSFORMED_DATA(state, data) {
      state.transformedData = data;
      safeJsonSave('msdat_transformed_data', data);
    },

    // Data Summary
    SET_DATA_SUMMARY(state, summary) {
      state.dataSummary = summary;
      safeJsonSave('msdat_data_summary', summary);
    },

    // Current Step
    SET_CURRENT_STEP(state, step) {
      state.currentStep = step;
      safeJsonSave('msdat_current_step', step);
    },

    // Active Instance ID
    SET_ACTIVE_INSTANCE_ID(state, id) {
      state.activeInstanceId = id;
      if (id) {
        safeJsonSave('msdat_active_instance', id);
      } else {
        localStorage.removeItem('msdat_active_instance');
      }
    },

    // User Account
    SET_USER_ACCOUNT(state, account) {
      state.userAccount = account;
      safeJsonSave('msdat_user_account', account);
    },

    // Instances
    SET_INSTANCES(state, instances) {
      state.instances = instances || [];
      safeJsonSave('msdat_instances', state.instances);
    },

    ADD_INSTANCE(state, instance) {
      const newInstance = {
        id: Date.now(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        ...instance,
      };
      state.instances.push(newInstance);
      safeJsonSave('msdat_instances', state.instances);
    },

    UPDATE_INSTANCE(state, { id, updates }) {
      const index = state.instances.findIndex((i) => i.id === id);
      if (index !== -1) {
        state.instances[index] = {
          ...state.instances[index],
          ...updates,
          updatedAt: new Date().toISOString(),
        };
        safeJsonSave('msdat_instances', state.instances);
      }
    },

    DELETE_INSTANCE(state, id) {
      state.instances = state.instances.filter((i) => i.id !== id);
      safeJsonSave('msdat_instances', state.instances);
    },

    // Reset all data
    RESET_CONFIGURER(state) {
      state.activeInstanceId = null;
      state.dashboardConfig = null;
      state.appearanceConfig = {
        theme: 'light',
        colors: {
          primary: '#007bff',
          secondary: '#6c757d',
          accent: '#28a745',
          background: '#ffffff',
          text: '#212529',
        },
        logo: null,
        fontFamily: 'Inter, sans-serif',
      };
      state.rawExcelData = {};
      state.selectedSheet = null;
      state.columnMappingConfig = null;
      state.transformedData = [];
      state.dataSummary = null;
      state.sheetNames = [];
      state.currentStep = 1;

      // Clear localStorage
      const keysToRemove = [
        'msdat_config',
        'msdat_appearance',
        'msdat_raw_data',
        'msdat_selected_sheet',
        'msdat_column_mapping',
        'msdat_transformed_data',
        'msdat_data_summary',
        'msdat_sheet_names',
        'msdat_current_step',
        'msdat_active_instance',
      ];
      keysToRemove.forEach((key) => removeLocalItem(key));
    },
  },

  actions: {
    // Load configuration from external source
    loadConfig({ commit }, config) {
      commit('SET_DASHBOARD_CONFIG', config);
    },

    // Process uploaded Excel file
    processExcelUpload({ commit }, { sheets, sheetNames }) {
      commit('SET_RAW_EXCEL_DATA', sheets);
      commit('SET_SHEET_NAMES', sheetNames);
      commit('SET_COLUMN_MAPPING', null);
      commit('SET_TRANSFORMED_DATA', []);
      commit('SET_DATA_SUMMARY', null);
      if (sheetNames.length > 0) {
        commit('SET_SELECTED_SHEET', sheetNames[0]);
      }
    },

    // Create a new dashboard instance
    async createInstance({ commit, state }) {
      const firebaseUser = await getFirebaseUser();
      if (!firebaseUser) {
        throw new Error('Please sign in again before creating a dashboard.');
      }

      const userId = firebaseUser.uid;

      const instance = {
        userId,
        user: userId,
        userid: userId,
        ownerId: userId,
        ownerEmail: firebaseUser.email || '',
        config: state.dashboardConfig,
        appearance: state.appearanceConfig,
        data: state.transformedData,
        dataSummary: state.dataSummary,
        columnMapping: state.columnMappingConfig,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      try {
        if (state.activeInstanceId) {
          await db.collection(DASHBOARD_COLLECTION).doc(state.activeInstanceId).update({
            ...instance,
            updatedAt: new Date().toISOString(),
          });
          const updatedInstance = { id: state.activeInstanceId, ...instance };
          commit('UPDATE_INSTANCE', { id: state.activeInstanceId, updates: updatedInstance });
          commit('SET_ACTIVE_INSTANCE_ID', null); // Reset on finish
          return updatedInstance;
        }

        const docRef = await db.collection(DASHBOARD_COLLECTION).add(instance);
        const newInstance = { id: docRef.id, ...instance };
        commit('ADD_INSTANCE', newInstance);
        return newInstance;
      } catch (e) {
        console.error('Error adding document to Firestore: ', e);
        if (isPermissionDenied(e)) {
          const details = [
            `Collection: ${DASHBOARD_COLLECTION}`,
            `uid: ${userId}`,
            'Expected rule to allow authenticated create/update where ownerId/userId/user equals request.auth.uid.',
          ].join(' | ');
          throw new Error(`Missing or insufficient Firestore permissions. ${details}`);
        }
        throw e;
      }
    },

    // Fetch all instances from Firebase
    async fetchInstances({ commit, rootState }) {
      try {
        const firebaseUser = await getFirebaseUser();
        if (!firebaseUser) {
          commit('SET_INSTANCES', []);
          return [];
        }

        const isAdmin = rootState?.configurerAuth?.isAdmin;
        const userId = firebaseUser.uid;

        const instancesRef = db.collection(DASHBOARD_COLLECTION);
        // Admins see all dashboards; regular users only their own.
        // Some projects enforce ownership on ownerId or user; try compatible fields.
        let querySnapshot;
        if (isAdmin) {
          querySnapshot = await instancesRef.get();
        } else {
          const queries = [
            instancesRef.where('ownerId', '==', userId),
            instancesRef.where('userId', '==', userId),
            instancesRef.where('user', '==', userId),
          ];

          const queryResults = await Promise.allSettled(
            queries.map((query) => query.get()),
          );
          const successfulQuery = queryResults.find((result) => result.status === 'fulfilled');
          if (successfulQuery) {
            // eslint-disable-next-line prefer-destructuring
            querySnapshot = successfulQuery.value;
          } else {
            const firstRejection = queryResults.find((result) => result.status === 'rejected');
            throw (firstRejection ? firstRejection.reason : new Error('No matching dashboard queries found.'));
          }
        }

        const instances = [];
        querySnapshot.forEach((docSnap) => {
          instances.push({ id: docSnap.id, ...docSnap.data() });
        });

        // Sort by createdAt descending
        instances.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        commit('SET_INSTANCES', instances);
        return instances;
      } catch (e) {
        console.error('Error fetching documents from Firestore: ', e);
        return [];
      }
    },

    // Load an existing instance
    async loadInstance({ commit, state }, instanceId) {
      const firebaseUser = await getFirebaseUser();
      if (!firebaseUser && instanceId !== 'preview') {
        throw new Error('Please sign in again before opening this dashboard.');
      }

      let instance = state.instances.find((i) => i.id === instanceId);

      if (!instance) {
        try {
          const docSnap = await db.collection(DASHBOARD_COLLECTION).doc(instanceId).get();
          if (docSnap.exists) {
            instance = { id: docSnap.id, ...docSnap.data() };
            // Optional: push to local state cache
            commit('ADD_INSTANCE', instance);
          }
        } catch (e) {
          console.error('Error loading document from Firestore: ', e);
        }
      }

      if (instance) {
        commit('SET_ACTIVE_INSTANCE_ID', instance.id);
        commit('SET_DASHBOARD_CONFIG', instance.config);
        commit('SET_APPEARANCE_CONFIG', instance.appearance);
        commit('SET_TRANSFORMED_DATA', instance.data);
        commit('SET_DATA_SUMMARY', instance.dataSummary);
        commit('SET_COLUMN_MAPPING', instance.columnMapping);
        return instance;
      }
      return null;
    },

    // Delete a dashboard instance from Firestore
    async deleteInstance({ commit }, instanceId) {
      try {
        const firebaseUser = await getFirebaseUser();
        if (!firebaseUser) {
          throw new Error('Please sign in again before deleting this dashboard.');
        }

        await db.collection(DASHBOARD_COLLECTION).doc(instanceId).delete();
        commit('DELETE_INSTANCE', instanceId);
        return true;
      } catch (e) {
        console.error('Error deleting document from Firestore: ', e);
        throw e;
      }
    },

    // Reset and start fresh
    resetConfigurer({ commit }) {
      commit('RESET_CONFIGURER');
    },
  },

  getters: {
    // Get instance by ID
    getInstanceById: (state) => (id) => state.instances.find((i) => i.id === id),

    // Check if data is ready for visualization
    isDataReady: (state) => state.transformedData && state.transformedData.length > 0,

    // Check if configuration is complete
    isConfigComplete: (state) => state.dashboardConfig
      && state.appearanceConfig
      && state.transformedData.length > 0
      && state.columnMappingConfig,

    // Get all unique indicators from transformed data
    availableIndicators: (state) => {
      if (!state.transformedData || state.transformedData.length === 0) return [];
      return [...new Set(state.transformedData.map((row) => row.indicator))];
    },

    // Get all unique locations from transformed data
    availableLocations: (state) => {
      if (!state.transformedData || state.transformedData.length === 0) return [];
      return [...new Set(state.transformedData.map((row) => row.location))];
    },

    // Get all unique periods from transformed data
    availablePeriods: (state) => {
      if (!state.transformedData || state.transformedData.length === 0) return [];
      return [...new Set(state.transformedData.map((row) => row.period))].sort();
    },

    // Get all unique datasources from transformed data
    availableDatasources: (state) => {
      if (!state.transformedData || state.transformedData.length === 0) return [];
      return [...new Set(state.transformedData.map((row) => row.datasource))];
    },

    // Get total instance count
    instanceCount: (state) => state.instances.length,
  },
};
